<template>
  <ion-page>
    <ion-header>
      <ion-toolbar @click="dismiss">
        <ion-title>{{ media?.originalFilename }}</ion-title>
        <ion-buttons slot="end">
          <ion-button>
            <ion-icon :icon="closeOutline" />
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content>
      <div v-if="media" class="ion-padding">
        <div v-if="mediaLoading && media.resourceType === 'image'" class="media-loading">
          <ion-spinner name="crescent" />
          <p>Loading media...</p>
        </div>
        <div
          v-if="media.resourceType === 'image'"
          class="zoom-container"
          @touchstart="handleTouchStart"
          @touchmove="handleTouchMove"
          @touchend="handleTouchEnd"
          @click="handleDoubleClick"
        >
          <img
            :src="getImageLargeUrl(media)"
            :class="['viewer-image', { 'media-hidden': mediaLoading }]"
            :style="getTransformStyle()"
            alt="Media viewer"
            @load="mediaLoading = false"
            @error="mediaLoading = false"
            @dragstart.prevent
          >
        </div>
        <p v-if="media.resourceType === 'image'" style="text-align: center; font-size: 0.85rem; color: var(--ion-color-medium); margin: 0.5rem 0 1rem 0; font-style: italic;">
          Double tap to zoom
        </p>
        <AdvancedVideo
          v-else-if="media.resourceType === 'video'"
          :cld-vid="getVideoLarge(media.publicId)"
          :class="['viewer-video', { 'media-hidden': mediaLoading }]"
          controls
          autoplay
          @loadeddata="mediaLoading = false"
          @error="mediaLoading = false"
        />
        <div>
          <p><strong>Uploaded:</strong> {{ formatDate(media.uploadedAt) }}</p>
          <p v-if="media.duration"><strong>Duration:</strong> {{ Math.round(media.duration) }}s</p>

          <ion-button
            expand="block"
            fill="outline"
            @click="downloadMedia"
          >
            <ion-icon slot="start" :icon="downloadOutline" />
            Download Media
          </ion-button>

          <ion-button
            v-if="currentUserType === 'instructor'"
            expand="block"
            color="danger"
            fill="outline"
            @click="deleteMediaItem"
          >
            <ion-icon slot="start" :icon="trashOutline" />
            Delete Media
          </ion-button>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script setup>
import { IonButton, IonButtons, IonContent, IonHeader, IonIcon, IonPage, IonSpinner, IonTitle, IonToolbar, modalController } from '@ionic/vue'
import { closeOutline, downloadOutline, trashOutline } from 'ionicons/icons'
import { ref } from 'vue'

import { AdvancedVideo } from '@cloudinary/vue'
import { formatDistanceToNow } from 'date-fns'

import { useCloudinaryUrls } from './useCloudinaryUrls'
import { useImageZoom } from './useImageZoom'

const props = defineProps({
  media: { type: Object, required: true },
  currentUserType: { type: String, required: true },
  onDownload: { type: Function, required: true },
  onDelete: { type: Function, required: true },
})

const mediaLoading = ref(true)

// URL generation
const { getImageLargeUrl, getVideoLarge } = useCloudinaryUrls()

// Image zoom functionality
const { handleTouchStart, handleTouchMove, handleTouchEnd, handleDoubleClick, getTransformStyle } = useImageZoom()

async function dismiss() {
  await modalController.dismiss()
}

async function downloadMedia() {
  await props.onDownload(props.media)
}

async function deleteMediaItem() {
  await props.onDelete(props.media)
  await modalController.dismiss()
}

function formatDate(timestamp) {
  if (!timestamp) return ''
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)

  try {
    const result = formatDistanceToNow(date, { addSuffix: true })
    return result.replace(/minutes?/g, 'min').replace(/hours?/g, 'hr')
  } catch (error) {
    console.error('Error formatting date:', error)
    return date.toLocaleDateString()
  }
}
</script>

<style scoped>
.media-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: var(--ion-color-medium);
}

.zoom-container {
  width: 100%;
  height: auto;
  overflow: hidden;
  touch-action: none;
  display: flex;
  justify-content: center;
  align-items: center;
}

.media-hidden {
  display: none;
}

.viewer-image,
.viewer-video {
  max-width: 100%;
  max-height: 80vh;
  width: auto;
  height: auto;
  object-fit: contain;
  margin: 0 auto 1rem auto;
  display: block;
}
</style>
